import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./Image.scss";
import { useDropzone } from "react-dropzone";
import { Icon } from "../../graphic/Icon";
import { ProgressCircle } from "../../loader/ProgressCircle";
import { iconList } from "../../graphic/Icon";
import { Spinner } from "../../loader/Spinner";
import { capitalize } from "../../../utils/stringUtils";

export const Image = ({
    src,
    busy,
    percent,
    thumbnail,
    scale,
    mod,
    placeholder,
    placeholderIcon,
    placeholderText,
    iconColor,
    backgroundColor,
    focalPoint,
    onClick,
    onFileSelect,
    disableFileDrop,
    multiple,
    alt,
    elementRef,
    ...props
}) => {
    const { getRootProps, getInputProps, open, isFocused, isDragAccept } = useDropzone({
        onDrop: (files) => onFileSelect(multiple ? files : files[0]),
        multiple: multiple,
        accept: {
            "image/*": []
        },
        disabled: percent !== null,
        noDrag: disableFileDrop,
        noClick: !!onClick
    });

    const handleClick = () => {
        if (onClick) {
            onClick();
        } else if (onFileSelect) {
            open();
        }
    };

    return (
        <div
            ref={elementRef}
            className={classNames(styles.base, {
                // Sizes
                [styles.thumbnail]: thumbnail,
                [styles.thumbSmall]: thumbnail === "small",
                [styles.thumbRegular]: thumbnail === "regular",
                [styles.thumbMedium]: thumbnail === "medium",
                [styles.thumbLarge]: thumbnail === "large",

                // Scales
                [styles.scaleFill]: scale === "fill",
                [styles.scaleSquare]: scale === "square",
                [styles.scaleA16_9]: scale === "16:9",

                // Mods
                [styles.modRounded]: mod && mod.indexOf("rounded") >= 0,
                [styles.modUnround]: mod && mod.indexOf("unround") >= 0,
                [styles.modOval]: mod && (mod.indexOf("oval") >= 0 || mod.indexOf("circle") >= 0),
                [styles.modGray]: mod && mod.indexOf("gray") >= 0,
                [styles.modShine]: mod && mod.indexOf("shine") >= 0,

                // File select
                [styles.isClickable]: onFileSelect || onClick,
                [styles.isFocused]: isFocused || isDragAccept,

                // Background color
                [backgroundColor && styles[`background${capitalize(backgroundColor)}`]]: backgroundColor
            })}
            onClick={handleClick}
        >
            {/* Wrapper */}
            <div className={styles.wrapper}>
                {/* Percent loaded */}
                {percent || busy ? (
                    <div className={styles.progress}>
                        <div className={styles.progressBox}>
                            {percent ? (
                                <ProgressCircle
                                    strong
                                    percent={percent}
                                    value=" "
                                    annotation={null}
                                    strokeWidth={8}
                                    trailWidth={7}
                                />
                            ) : (
                                <Spinner size="auto" />
                            )}
                        </div>
                    </div>
                ) : null}

                {/* Image / Placeholder */}
                {src ? (
                    <div className={styles.image}>
                        {mod && mod.indexOf("shine") >= 0 ? <div className={styles.shine}></div> : null}
                        <img
                            alt={alt}
                            src={src}
                            {...props}
                            style={
                                focalPoint?.x
                                    ? { objectPosition: `${focalPoint?.x}% ${focalPoint?.y}%` }
                                    : { objectPosition: "center center" }
                            }
                        />
                    </div>
                ) : percent == null && !busy ? (
                    <div
                        className={classNames(styles.placeholder, {
                            [styles.default]: !placeholder
                        })}
                    >
                        {placeholder || (
                            <Icon className={styles.icon} name={placeholderIcon} color={iconColor} size="auto" />
                        )}
                        {placeholderText ? <div className={styles.text}>{placeholderText}</div> : null}
                    </div>
                ) : null}
            </div>

            {/* File select dropzone */}
            {onFileSelect ? (
                <div className={styles.dropzone} {...getRootProps()}>
                    <input className={styles.input} {...getInputProps()} />
                </div>
            ) : null}
        </div>
    );
};

Image.defaultProps = {
    percent: null,
    placeholderIcon: "image",
    multiple: false
};

Image.propTypes = {
    /**
     * Image url
     */
    src: PropTypes.string,
    /**
     * Show busy indicator
     */
    busy: PropTypes.bool,
    /**
     * Show loading indicator with percent progress
     */
    percent: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    /**
     * Display thumbnail sized picture
     */
    thumbnail: PropTypes.oneOf(["small", "medium", "regular", "large"]),
    /**
     * Scale picture
     */
    scale: PropTypes.oneOf(["fill", "square", "16:9"]),
    /**
     * Apply modification to image. One or more of "rounded", "oval", "gray".
     */
    mod: PropTypes.string,
    /**
     * Placeholder if no image
     */
    placeholder: PropTypes.any,
    /**
     * Placeholder if no image
     */
    placeholderIcon: PropTypes.oneOf(iconList),
    /**
     * Placeholder text
     */
    placeholderText: PropTypes.any,
    /**
     * Color of placeholder icon
     */
    iconColor: PropTypes.string,
    /**
     * Background color of placeholder
     */
    backgroundColor: PropTypes.string,
    /**
     * Click handler
     */
    onClick: PropTypes.func,
    /**
     * File select on click
     */
    onFileSelect: PropTypes.func,
    /**
     * Disable file drop when file select is active
     */
    disableFileDrop: PropTypes.bool,
    /**
     * Select multiple files
     */
    multiple: PropTypes.bool,
    /**
     * Image "alt" text
     */
    alt: PropTypes.string
};
